/**
 * refreshTimeRange — NWT-local time-of-day gating for layer auto-refresh.
 *
 * Layer config can include `refreshTimeRange: "HH:MM-HH:MM"` alongside
 * `refreshInterval` (ms). When set, the layer's interval tick only fires a
 * refresh when the current America/Yellowknife time is within the range.
 *
 * Cross-midnight ranges are supported — `22:00-06:00` is read as "active
 * when now >= 22:00 OR now <= 06:00".
 *
 * Ranges are half-open: start inclusive, end exclusive. `13:30-14:00`
 * includes 13:30 and 13:59 but NOT 14:00 sharp. This avoids double-firing
 * at the boundary when the timer tick happens to land exactly on the end.
 */

const NWT_TZ = 'America/Yellowknife';

export interface ParsedRange {
  startMin: number; // minutes since NWT midnight (0..1439)
  endMin: number;
  crossesMidnight: boolean;
}

function parseHHMM(s: string): number | null {
  const m = /^([0-2]?\d):([0-5]\d)$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * Parse `"HH:MM-HH:MM"`. Returns null on invalid input (caller decides
 * whether to log; this fn is pure for cheap reuse).
 */
export function parseRefreshTimeRange(s: string | null | undefined): ParsedRange | null {
  if (!s) return null;
  const dash = s.indexOf('-');
  if (dash < 0) return null;
  const startMin = parseHHMM(s.slice(0, dash).trim());
  const endMin = parseHHMM(s.slice(dash + 1).trim());
  if (startMin === null || endMin === null) return null;
  if (startMin === endMin) return null; // empty / always-off — caller likely a typo
  return { startMin, endMin, crossesMidnight: startMin > endMin };
}

function nwtMinutesSinceMidnight(now: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: NWT_TZ,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now);
  const h = Number(parts.find(p => p.type === 'hour')?.value ?? '0');
  const m = Number(parts.find(p => p.type === 'minute')?.value ?? '0');
  return h * 60 + m;
}

/**
 * True if `now` (defaults to Date.now()) is inside the parsed range,
 * interpreted in NWT-local time.
 *
 * Accepts either a parsed range or the raw config string. Bad strings are
 * treated as "no gate" (returns true) — fail open so a typo doesn't silently
 * shut down a layer.
 */
export function isInRefreshTimeRange(
  range: string | ParsedRange | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!range) return true;
  const parsed = typeof range === 'string' ? parseRefreshTimeRange(range) : range;
  if (!parsed) return true;
  const minNow = nwtMinutesSinceMidnight(now);
  if (parsed.crossesMidnight) {
    return minNow >= parsed.startMin || minNow < parsed.endMin;
  }
  return minNow >= parsed.startMin && minNow < parsed.endMin;
}
